import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Calendar, MapPin, User, CheckCircle, Clock, XCircle,
  Phone, Youtube, Building2, Mail, FileText, AlertCircle, ExternalLink, Link,
} from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getMyProjects, getMyProfile, switchRole } from '../services/api';
import { toast } from 'react-toastify';

function ProfileRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
      <div style={{ color: '#be185d', marginTop: '2px', flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '14px', color: '#1f2937' }}>{value}</div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  upcoming:    { color: '#3b82f6', bg: '#eff6ff' },
  in_progress: { color: '#f59e0b', bg: '#fffbeb' },
  completed:   { color: '#22c55e', bg: '#f0fdf4' },
  cancelled:   { color: '#ef4444', bg: '#fef2f2' },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ev = new Date(dateStr); ev.setHours(0, 0, 0, 0);
  const diff = Math.round((ev - today) / 86400000);
  if (diff === 0) return 'Today!';
  if (diff > 0) return `${diff}d away`;
  return `${Math.abs(diff)}d ago`;
}

// Where a former team member can still go (they may also plan a wedding or help with one)
const OTHER_DASHBOARD_HOME = { couple: '/dashboard', helper: '/checklist' };
const OTHER_DASHBOARD_LABEL = { couple: 'Go to My Wedding', helper: 'Go to Helper Dashboard' };

function EmployeeDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const { t } = useLanguage();
  const STATUS_CONFIG = {
    upcoming:    { label: t('vendorTeam.statusUpcoming'),    ...STATUS_COLORS.upcoming },
    in_progress: { label: t('vendorTeam.statusInProgress'),  ...STATUS_COLORS.in_progress },
    completed:   { label: t('vendorTeam.statusCompleted'),   ...STATUS_COLORS.completed },
    cancelled:   { label: t('vendorTeam.statusCancelled'),   ...STATUS_COLORS.cancelled },
  };
  const navigate = useNavigate();

  const [activeTab, setActiveTab]           = useState('projects');
  const [projects, setProjects]             = useState([]);
  const [employeeInfo, setEmployeeInfo]     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [expandedProject, setExpandedProject] = useState(null);
  const [projFilter, setProjFilter]         = useState('all');

  const [profile, setProfile]               = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const profileLoadedRef                    = useRef(false);
  const [noAccess, setNoAccess]             = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'vendor_employee') { navigate('/'); return; }

    getMyProjects()
      .then(res => {
        setProjects(res.data.projects || []);
        setEmployeeInfo(res.data.employee || null);
      })
      .catch(err => {
        // 403 = still signed in, but no longer on a team (access revoked).
        // Redirecting to /login looped forever: the login page sends a
        // signed-in team member straight back here.
        if (err.response?.status === 403) setNoAccess(true);
        else setError(err.response?.data?.error || 'Failed to load projects');
      })
      .finally(() => setLoading(false));
  }, [user, navigate, authLoading]);

  async function openOtherDashboard(role) {
    try {
      const res = await switchRole(role);
      localStorage.setItem('nepwed_token', res.data.token);
      // Full navigation so AuthContext re-fetches /me with the new active role
      window.location.href = OTHER_DASHBOARD_HOME[role] || '/';
    } catch {
      toast.error('Could not open that dashboard. Please sign in again.');
    }
  }

  async function loadProfile() {
    if (profileLoadedRef.current) return;
    setProfileLoading(true);
    try {
      const res = await getMyProfile();
      setProfile(res.data);
      profileLoadedRef.current = true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }

  function switchTab(tab) {
    setActiveTab(tab);
    if (tab === 'profile') loadProfile();
  }

  const formatDate = dateStr => {
    if (!dateStr) return 'Date TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  const upcoming   = projects.filter(p => p.status === 'upcoming').length;
  const inProgress = projects.filter(p => p.status === 'in_progress').length;
  const completed  = projects.filter(p => p.status === 'completed').length;

  if (loading) return (
    <div className="vendor-portal">
      <div className="portal-container">
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div className="spinner"></div>
          <p style={{ color: '#6b7280', marginTop: '12px' }}>{t('common.loading')}…</p>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="vendor-portal">
      <div className="portal-container">
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <XCircle size={48} color="#ef4444" />
          <p style={{ color: '#dc2626', marginTop: '12px' }}>{error}</p>
        </div>
      </div>
    </div>
  );

  if (noAccess) {
    const otherRoles = (user?.availableRoles || []).filter(r => r !== 'vendor_employee' && OTHER_DASHBOARD_HOME[r]);
    return (
      <div className="vendor-portal">
        <div className="portal-container">
          <div style={{ textAlign: 'center', padding: '80px 20px', maxWidth: '520px', margin: '0 auto' }}>
            <AlertCircle size={48} color="#f59e0b" />
            <h2 style={{ margin: '16px 0 8px', fontSize: '1.3rem', color: '#1f2937' }}>You're no longer on a vendor team</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: 1.6 }}>
              Your access to the team's projects has been removed. If this is a mistake, ask the vendor to send you a new invitation.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
              {otherRoles.map(r => (
                <button key={r} className="btn btn-primary" onClick={() => openOtherDashboard(r)}>
                  {OTHER_DASHBOARD_LABEL[r]}
                </button>
              ))}
              <button className="btn btn-outline" onClick={() => { logout(); navigate('/login'); }}>
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filtered = projFilter === 'all' ? projects : projects.filter(p => p.status === projFilter);

  return (
    <div className="vendor-portal">
      <div className="portal-container">

        {/* ===== HEADER ===== */}
        <div className="portal-header">
          <div className="portal-header-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '6px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Building2 size={24} color="#be185d" />
              </div>
              <div>
                <h1 className="portal-title" style={{ marginBottom: '2px' }}>
                  {employeeInfo?.vendorName || 'My Dashboard'}
                </h1>
                <p className="portal-category" style={{ margin: 0 }}>
                  {user?.first_name ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}` : user?.email}
                  {employeeInfo?.jobTitle ? ` · ${employeeInfo.jobTitle}` : ''}
                </p>
              </div>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fce7f3', color: '#be185d', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px' }}>
              <User size={11} /> {t('vendorTeam.teamMemberBadge')}
            </div>
          </div>
          <div className="portal-header-stats">
            <div className="portal-stat">
              <Briefcase size={18} />
              <span className="portal-stat-value">{projects.length}</span>
              <span className="portal-stat-label">Total</span>
            </div>
            <div className="portal-stat">
              <Clock size={18} color="#3b82f6" />
              <span className="portal-stat-value">{upcoming}</span>
              <span className="portal-stat-label">Upcoming</span>
            </div>
            <div className="portal-stat">
              <Calendar size={18} color="#f59e0b" />
              <span className="portal-stat-value">{inProgress}</span>
              <span className="portal-stat-label">In Progress</span>
            </div>
            <div className="portal-stat">
              <CheckCircle size={18} color="#22c55e" />
              <span className="portal-stat-value">{completed}</span>
              <span className="portal-stat-label">Done</span>
            </div>
          </div>
        </div>

        {/* ===== TABS ===== */}
        <div className="portal-tabs">
          <button className={`portal-tab ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => switchTab('projects')}>
            <Briefcase size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {t('vendorTeam.myDashboardTitle')}
          </button>
          <button className={`portal-tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => switchTab('profile')}>
            <User size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {t('vendorTeam.myProfile')}
          </button>
        </div>

        {/* ===== PROJECTS TAB ===== */}
        {activeTab === 'projects' && (
          <div>
            {/* Status filter */}
            <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '10px', padding: '3px', marginBottom: '16px', width: 'fit-content' }}>
              {[
                ['all', 'All', projects.length],
                ['upcoming', 'Upcoming', upcoming],
                ['in_progress', 'In Progress', inProgress],
                ['completed', 'Completed', completed],
              ].map(([val, label, count]) => (
                <button key={val} onClick={() => setProjFilter(val)}
                  style={{ padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-body)', transition: 'all 0.15s',
                    background: projFilter === val ? '#fff' : 'transparent',
                    color: projFilter === val ? '#be185d' : '#6b7280',
                    boxShadow: projFilter === val ? '0 1px 3px rgba(0,0,0,.1)' : 'none' }}>
                  {label} <span style={{ opacity: 0.7 }}>({count})</span>
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: '12px' }}>
                <Calendar size={48} color="#d1d5db" />
                <h3 style={{ color: '#6b7280', marginTop: '16px' }}>
                  {projects.length === 0 ? t('vendorTeam.noProjectsAssigned') : `No ${projFilter === 'all' ? '' : STATUS_CONFIG[projFilter]?.label + ' '}projects`}
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                  {projects.length === 0 ? t('vendorTeam.noProjectsAssignedText') : t('common.tryDifferentFilter')}
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '14px' }}>
                {filtered.map(project => {
                  const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.upcoming;
                  const isExpanded = expandedProject === project.id;
                  const days = daysUntil(project.event_date);
                  const pkg = project.package_details || {};

                  return (
                    <div key={project.id} className="portal-info-card"
                      style={{ padding: '20px 24px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                      onClick={() => setExpandedProject(isExpanded ? null : project.id)}>

                      {/* Card header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1f2937' }}>{project.title}</h3>
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                            {days && project.status !== 'completed' && project.status !== 'cancelled' && (
                              <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '3px',
                                background: days.includes('ago') ? '#fef2f2' : days === 'Today!' ? '#fef9c3' : '#eff6ff',
                                color: days.includes('ago') ? '#ef4444' : days === 'Today!' ? '#ca8a04' : '#3b82f6' }}>
                                <Clock size={10} /> {days}
                              </span>
                            )}
                            {project.role_on_project && (
                              <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: '#fce7f3', color: '#be185d' }}>
                                {project.role_on_project}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', color: '#6b7280', fontSize: '13px' }}>
                            {project.client_name && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <User size={12} color="#9ca3af" /> {project.client_name}
                              </span>
                            )}
                            {project.event_date && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <Calendar size={12} color="#9ca3af" /> {formatDate(project.event_date)}
                              </span>
                            )}
                            {project.location && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <MapPin size={12} color="#9ca3af" /> {project.location}
                              </span>
                            )}
                          </div>

                          {/* Delivery badges (always visible) */}
                          {(pkg.photoHours || pkg.videoHours || project.photo_delivered || project.video_delivered) && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                              {(pkg.photoHours || project.photo_delivered) && (
                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                  background: project.photo_delivered ? '#f0fdf4' : '#f9fafb',
                                  color: project.photo_delivered ? '#16a34a' : '#9ca3af' }}>
                                  📷{pkg.photoHours ? ` ${pkg.photoHours}h ·` : ''} {project.photo_delivered ? 'Delivered' : 'Pending'}
                                </span>
                              )}
                              {(pkg.videoHours || project.video_delivered) && (
                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                  background: project.video_delivered ? '#f0fdf4' : '#f9fafb',
                                  color: project.video_delivered ? '#16a34a' : '#9ca3af' }}>
                                  🎥{pkg.videoHours ? ` ${pkg.videoHours}h ·` : ''} {project.video_delivered ? 'Delivered' : 'Pending'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>
                          {isExpanded ? `▲ ${t('vendorTeam.collapseDetails')}` : `▼ ${t('vendorTeam.expandDetails')}`}
                        </span>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div style={{ marginTop: '18px', borderTop: '1px solid #f3f4f6', paddingTop: '18px' }}
                          onClick={e => e.stopPropagation()}>

                          {/* Description */}
                          {project.description && (
                            <div style={{ marginBottom: '14px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <FileText size={12} /> Description
                              </div>
                              <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>{project.description}</p>
                            </div>
                          )}

                          {/* Notes */}
                          {project.notes && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px 16px', marginBottom: '14px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#92400e', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <AlertCircle size={12} /> {t('vendorTeam.notesFromManager')}
                              </div>
                              <p style={{ margin: 0, fontSize: '13px', color: '#78350f', lineHeight: '1.6' }}>{project.notes}</p>
                            </div>
                          )}

                          {/* Package details */}
                          {(pkg.photoHours || pkg.videoHours || pkg.audioRecording || pkg.droneCoverage || pkg.fullCeremony || pkg.highlightsVideo || pkg.additionalComponents) && (
                            <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('vendorTeam.packageInclusions')}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {pkg.photoHours && (
                                  <span style={{ fontSize: '12px', background: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                    📷 {pkg.photoHours}h photo
                                  </span>
                                )}
                                {pkg.videoHours && (
                                  <span style={{ fontSize: '12px', background: '#fef3c7', color: '#d97706', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                    🎥 {pkg.videoHours}h video
                                  </span>
                                )}
                                {pkg.audioRecording && (
                                  <span style={{ fontSize: '12px', background: '#f0fdf4', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                    🎙️ Audio
                                  </span>
                                )}
                                {pkg.droneCoverage && (
                                  <span style={{ fontSize: '12px', background: '#f0fdf4', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                    🚁 Drone
                                  </span>
                                )}
                                {pkg.fullCeremony && (
                                  <span style={{ fontSize: '12px', background: '#f0fdf4', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                    ⛪ Full Ceremony
                                  </span>
                                )}
                                {pkg.highlightsVideo && (
                                  <span style={{ fontSize: '12px', background: '#f0fdf4', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                    🎬 Highlights
                                  </span>
                                )}
                              </div>
                              {pkg.additionalComponents && (
                                <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                                  <span style={{ fontWeight: 600 }}>Additional: </span>{pkg.additionalComponents}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Delivery links (final - for both roles) */}
                          {(project.photo_delivered || project.video_delivered || project.photo_delivery_link || project.video_delivery_link) && (
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#15803d', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                ✅ {t('vendorTeam.deliveredFiles')}
                              </div>
                              <div style={{ display: 'grid', gap: '8px' }}>
                                {(project.photo_delivered || project.photo_delivery_link) && (() => {
                                  const role = (project.role_on_project || '').toLowerCase();
                                  const empType = (project.employee_type || employeeInfo?.employeeType || '').toLowerCase();
                                  const isVideoOnly = (role.includes('video') && !role.includes('photo')) || (empType === 'videographer');
                                  if (isVideoOnly) return null;
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600, minWidth: '60px' }}>📷 Photos</span>
                                      {project.photo_delivery_link ? (
                                        <a href={project.photo_delivery_link} target="_blank" rel="noopener noreferrer"
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#be185d', fontSize: '13px', textDecoration: 'none', fontWeight: 600 }}
                                          onMouseOver={e => e.currentTarget.style.textDecoration='underline'}
                                          onMouseOut={e => e.currentTarget.style.textDecoration='none'}>
                                          <Link size={13} /> View Photos <ExternalLink size={11} />
                                        </a>
                                      ) : (
                                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>Link not yet shared</span>
                                      )}
                                    </div>
                                  );
                                })()}
                                {(project.video_delivered || project.video_delivery_link) && (() => {
                                  const role = (project.role_on_project || '').toLowerCase();
                                  const empType = (project.employee_type || employeeInfo?.employeeType || '').toLowerCase();
                                  const isPhotoOnly = (role.includes('photo') && !role.includes('video')) || (empType === 'photographer');
                                  if (isPhotoOnly) return null;
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600, minWidth: '60px' }}>🎥 Videos</span>
                                      {project.video_delivery_link ? (
                                        <a href={project.video_delivery_link} target="_blank" rel="noopener noreferrer"
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#be185d', fontSize: '13px', textDecoration: 'none', fontWeight: 600 }}
                                          onMouseOver={e => e.currentTarget.style.textDecoration='underline'}
                                          onMouseOut={e => e.currentTarget.style.textDecoration='none'}>
                                          <Link size={13} /> View Videos <ExternalLink size={11} />
                                        </a>
                                      ) : (
                                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>Link not yet shared</span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Raw upload links (role-based) */}
                          {(project.raw_photo_link || project.raw_video_link) && (() => {
                            const role = (project.role_on_project || '').toLowerCase();
                            const empType = (project.employee_type || employeeInfo?.employeeType || '').toLowerCase();
                            const isVideoOnly = (role.includes('video') && !role.includes('photo')) || empType === 'videographer';
                            const isPhotoOnly = (role.includes('photo') && !role.includes('video')) || empType === 'photographer';
                            const showPhoto = !isVideoOnly && !!project.raw_photo_link;
                            const showVideo = !isPhotoOnly && !!project.raw_video_link;
                            if (!showPhoto && !showVideo) return null;
                            return (
                              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#1d4ed8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  📂 {t('vendorTeam.rawUploadLinks')}
                                </div>
                                <div style={{ display: 'grid', gap: '8px' }}>
                                  {showPhoto && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600, minWidth: '60px' }}>📷 Photos</span>
                                      <a href={project.raw_photo_link} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#1d4ed8', fontSize: '13px', textDecoration: 'none', fontWeight: 600 }}
                                        onMouseOver={e => e.currentTarget.style.textDecoration='underline'}
                                        onMouseOut={e => e.currentTarget.style.textDecoration='none'}>
                                        <Link size={13} /> Upload / Access Raw Photos <ExternalLink size={11} />
                                      </a>
                                    </div>
                                  )}
                                  {showVideo && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600, minWidth: '60px' }}>🎥 Videos</span>
                                      <a href={project.raw_video_link} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#1d4ed8', fontSize: '13px', textDecoration: 'none', fontWeight: 600 }}
                                        onMouseOver={e => e.currentTarget.style.textDecoration='underline'}
                                        onMouseOut={e => e.currentTarget.style.textDecoration='none'}>
                                        <Link size={13} /> Upload / Access Raw Videos <ExternalLink size={11} />
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Assigned at */}
                          {project.assigned_at && (
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              Assigned: {new Date(project.assigned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===== PROFILE TAB ===== */}
        {activeTab === 'profile' && (
          <div>
            {profileLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="spinner"></div>
                <p style={{ color: '#6b7280', marginTop: '12px' }}>Loading profile…</p>
              </div>
            ) : profile ? (
              <div style={{ display: 'grid', gap: '16px' }}>

                {/* Company card */}
                <div className="portal-info-card" style={{ padding: '20px 24px', background: 'linear-gradient(135deg, #fdf2f8, #fff)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Building2 size={26} color="#be185d" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '17px', color: '#1f2937' }}>{profile.vendor_name}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '3px' }}>
                        {profile.job_title && <span style={{ fontWeight: 600, color: '#be185d' }}>{profile.job_title}</span>}
                        {profile.vendor_city && <span> · {profile.vendor_city}</span>}
                        {profile.vendor_category && <span> · {profile.vendor_category}</span>}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {profile.project_count || 0} project{profile.project_count !== 1 ? 's' : ''} assigned
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal info card — READ ONLY */}
                <div className="portal-info-card" style={{ padding: '24px' }}>
                  <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={18} color="#be185d" /> Personal Profile
                  </h3>
                  <div style={{ display: 'grid', gap: '14px' }}>
                    <ProfileRow icon={<User size={14} />} label="Full Name"
                      value={[profile.first_name, profile.last_name].filter(Boolean).join(' ') || '—'} />
                    <ProfileRow icon={<Mail size={14} />} label="Email" value={profile.email || '—'} />
                    <ProfileRow icon={<Phone size={14} />} label="Phone" value={profile.phone || '—'} />
                    <ProfileRow icon={<Calendar size={14} />} label="Date of Birth"
                      value={profile.date_of_birth
                        ? new Date(profile.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                        : '—'} />
                    <ProfileRow icon={<MapPin size={14} />} label="Address" value={profile.address || '—'} />
                    {profile.employee_type && (
                      <ProfileRow icon={<Briefcase size={14} />} label="Type" value={profile.employee_type} />
                    )}
                    {profile.work_location && (
                      <ProfileRow icon={<MapPin size={14} />} label="Work Location" value={profile.work_location} />
                    )}
                    {profile.portfolio_url && (
                      <ProfileRow icon={<Link size={14} />} label="Portfolio"
                        value={
                          <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#be185d', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '14px', textDecoration: 'none' }}>
                            {profile.portfolio_url.replace(/^https?:\/\//, '')} <ExternalLink size={12} />
                          </a>
                        } />
                    )}
                    {profile.youtube_url && (
                      <ProfileRow icon={<Youtube size={14} color="#ef4444" />} label="YouTube"
                        value={
                          <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '14px', textDecoration: 'none' }}>
                            {profile.youtube_url.replace(/^https?:\/\//, '')} <ExternalLink size={12} />
                          </a>
                        } />
                    )}
                    {profile.bio && (
                      <div style={{ paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Bio</div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>{profile.bio}</p>
                      </div>
                    )}
                    {profile.emergency_contact && (
                      <ProfileRow icon={<AlertCircle size={14} color="#f59e0b" />} label="Emergency Contact" value={profile.emergency_contact} />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <User size={48} color="#d1d5db" />
                <p style={{ color: '#9ca3af', marginTop: '12px' }}>Profile not available. Please contact your manager.</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default EmployeeDashboard;
